<template>
  <div
    class="app-time-select"
    v-click-outside="
      () => {
        this.pickerEnabled = false
      }
    "
  >
    <div class="time-selector">
      <input
        type="text"
        :class="['form-control time', { 'is-invalid': !isValid_ }]"
        :value="timeLabel"
        @change="updateTime($event.target.value)"
        @keyup.enter="pickerEnabled = false"
        :disabled="disabled"
        @click="openPicker"
      />
      <i :class="['ion ion-ios-arrow-down arrow', { disabled: options.length === 0 }]" @click="openPicker"></i>
    </div>
    <div class="picker" v-if="pickerEnabled" @keyup.esc="pickerEnabled = false">
      <div class="options" ref="options">
        <div :class="['option', { active: option.active }]" v-for="(option) in options" :key="option.title" @click="optionSelect(option)">
          {{ option.title }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppTimeSelect',
  props: {
    time: {
      type: String,
      default: ''
    },
    optionsStep: {
      type: Number,
      default: 30
    },
    showInterval: {
      type: Boolean,
      default: true
    },
    toTime: {
      type: String,
      default: ''
    },  
    fromTime: {
      type: String,
      default: ''
    },
    isValid: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    isValidFormat: true,
    pickerEnabled: false,
    hour: 9,
    minute: 0
  }),
  computed: {
    isValid_() {
      return this.isValid && this.isValidFormat && this.isInRange
    },
    // fromTime/toTime used to only thin out the dropdown: a time typed straight into
    // the field sailed past them, so an end before its start never showed as invalid
    isInRange() {
      const total = this.hour * 60 + this.minute
      if (this.fromTime && total < this.fromTimeParsed.totalMinutes) return false
      if (this.toTime && total > this.toTimeParsed.totalMinutes) return false
      return true
    },
    timeLabel() {
      return `${this.hour}:${this.minute < 10 ? '0' : ''}${this.minute}`
    },
    fromTimeParsed() {
      const parsed = this.parseTime(this.fromTime)
      return parsed || { hour: 0, minute: 0, totalMinutes: 0 }
    },
    toTimeParsed() {
      const parsed = this.parseTime(this.toTime)
      return parsed || { hour: 23, minute: 59, totalMinutes: 23 * 60 + 59 }
    },
    options() {
      const options = []
      const dayMinutes = 1440
      const steps = Math.ceil(dayMinutes / this.optionsStep)
      for (let i = 0; i < steps; i++) {
        const stepTime = (i * this.optionsStep) / 60
        const hour = Math.floor(stepTime)
        const minute = parseInt(((stepTime - hour) * 60).toFixed(0))
        const active = this.hour === hour && this.minute === minute
        const optionTotalMinutes = hour * 60 + minute
        const fromTotalMinutes = this.fromTimeParsed.hour * 60 + this.fromTimeParsed.minute
        const toTotalMinutes = this.toTimeParsed.hour * 60 + this.toTimeParsed.minute
        
        if (optionTotalMinutes > fromTotalMinutes && optionTotalMinutes < toTotalMinutes) {
          options.push({ 
            title: `${hour}:${minute < 10 ? '0' : ''}${minute}${this.calcFromDiffTitle({ hour, minute })}`, 
            active,
            hour,
            minute
          })
        }
      }

      return options
    }
  },
  methods: {
    openPicker() {
      if (this.disabled) return
      this.pickerEnabled = true
      this.$nextTick(() => {
        let activeOption = this.$refs.options.getElementsByClassName('active')[0]
        if (activeOption) activeOption.scrollIntoView()
      })
    },
    calcFromDiffTitle(toTime) {
      if (this.fromTime) {
        const hourDiff = toTime.hour - this.fromTimeParsed.hour
        let minuteDiff = toTime.minute - this.fromTimeParsed.minute
        if (hourDiff > 0 || (hourDiff === 0 && minuteDiff >= 0)) {
          minuteDiff = minuteDiff / 60
          return ` (${(parseFloat(hourDiff) + parseFloat(minuteDiff)).toFixed(1)}h)`
        }
      }
      return ''
    },
    parseTime(time) {
      if (time) {
        let parts = time.split(':')
        if (parts.length === 2) {
          let hour = parseInt(parts[0])
          let minute = parseInt(parts[1])
          if (isNaN(hour) || isNaN(minute) || hour > 23 || hour < 0 || minute > 59 || minute < 0) {
            return false
          }
          return { minute, hour, totalMinutes: hour * 60 + minute }
        }
        return false
      }
    },
    updateTime(time) {
      let timeParsed = this.parseTime(time)
      if (timeParsed) {
        this.isValidFormat = true
        if (this.minute !== timeParsed.minute || this.hour !== timeParsed.hour) {
          this.minute = timeParsed.minute
          this.hour = timeParsed.hour
          this.$emit('change', timeParsed)
        }
      } else if (time) {
        this.isValidFormat = false
      }
    },
    optionSelect(option) {
      // Fix BUG #6: Parse time directly from hour and minute, not from title which may contain "(2.5h)"
      const timeString = `${option.hour}:${option.minute < 10 ? '0' : ''}${option.minute}`
      this.updateTime(timeString)
      this.pickerEnabled = false
    }
  },
  watch: {
    time: {
      immediate: true,
      handler(time) {
        this.updateTime(time)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.app-time-select {
  position: relative;
}

.time-selector {
  position: relative;
}

.time {
  padding-left: 7px;
  padding-right: 7px;
}

.arrow {
  position: absolute;
  right: 5px;
  top: 33%;
  cursor: pointer;

  &.disabled {
    opacity: 0.5;
  }
}

.picker {
  position: absolute;
  background: #fff;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.4);
  z-index: 1;
  right: 0;
}

.options {
  max-height: 250px;
  overflow-y: scroll;
  overflow-x: hidden;

  .option {
    padding: 5px 8px;
    display: block;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      background: #ccc;
    }
  }
}
</style>
